'use client';

import { useState, useEffect } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import { TeamsAllowedField } from '@/features/tournament-wizard/blocks';

interface EditTeamsAllowedModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMinTeams: number;
  currentMaxTeams: number;
  /** Used as lower bound for min teams (same as creation flow / fixture). */
  teamsPerGroups: number;
  /** Knockout-only: no groups, so the floor is the 2 teams a bracket needs. */
  knockoutOnly?: boolean;
  onSave: (teamsData: {
    minTeamsAllowed: number;
    maxTeamsAllowed: number;
  }) => Promise<boolean>;
  isLoading: boolean;
}

export function EditTeamsAllowedModal({
  isOpen,
  onClose,
  currentMinTeams,
  currentMaxTeams,
  teamsPerGroups,
  knockoutOnly = false,
  onSave,
  isLoading,
}: EditTeamsAllowedModalProps) {
  const [minTeams, setMinTeams] = useState(currentMinTeams);
  const [maxTeams, setMaxTeams] = useState(currentMaxTeams);

  useEffect(() => {
    if (isOpen) {
      setMinTeams(currentMinTeams);
      setMaxTeams(currentMaxTeams);
    }
  }, [isOpen, currentMinTeams, currentMaxTeams]);

  const minFloor = knockoutOnly ? 2 : Math.max(2, teamsPerGroups);

  const handleSave = async () => {
    if (minTeams > maxTeams) return;
    await onSave({ minTeamsAllowed: minTeams, maxTeamsAllowed: maxTeams });
  };

  const invalid = minTeams > maxTeams;

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Equipos Permitidos"
      onSave={handleSave}
      isLoading={isLoading}
      size="large"
      disabled={invalid}
    >
      <div className="py-4">
        <TeamsAllowedField
          minTeamsAllowed={minTeams}
          maxTeamsAllowed={maxTeams}
          onMinTeamsChange={setMinTeams}
          onMaxTeamsChange={setMaxTeams}
          minFloor={minFloor}
          wrapInCard={false}
          minInputId="edit-tournament-min-teams"
          maxInputId="edit-tournament-max-teams"
        />
      </div>
    </EditModal>
  );
}
